/**
 * Quy tắc cảnh báo audit của tenant — tham số `audit.alert_rules`, kiểu
 * `alert_rule_list` (ADR-0059 mục 4.1).
 *
 * Giá trị là một mảng quy tắc; audit-service đếm sự kiện khớp điều kiện trong
 * cửa sổ trượt, đạt ngưỡng thì tạo cảnh báo. Giao diện sửa mảng này trong bản
 * nháp của form cài đặt như mọi khoá khác — không có API ghi riêng.
 *
 * Luật của thư mục `nghiep-vu/`: không gọi HTTP, không dựng chuỗi hiển thị —
 * trả **mã** để view tự dịch.
 */
import type { BienTruong, MucDoThongBao } from './types.ts';

export type QuyetDinhAudit = 'ALLOW' | 'DENY';
export type NguonAudit = 'gateway' | 'service' | 'keycloak';
export type NhomTheo = 'user' | 'ip' | 'tenant';

/** Một quy tắc — giữ đúng tên trường của API; server trả đủ mọi trường. */
export interface QuyTacCanhBao {
  id: string;
  name: string;
  enabled: boolean;
  /** `null` là mọi quyết định. */
  decision: QuyetDinhAudit | null;
  /** Glob, `*` khớp mọi chuỗi; rỗng là mọi action. */
  actions: string[];
  /** `null` là mọi resource. */
  resource_prefix: string | null;
  /** Rỗng là mọi nguồn. */
  sources: NguonAudit[];
  /** Rỗng là không lọc theo mã lỗi. */
  error_codes: string[];
  group_by: NhomTheo;
  threshold: number;
  window_seconds: number;
  cooldown_seconds: number;
  severity: MucDoThongBao;
}

export type TruongQuyTac = keyof QuyTacCanhBao;

export const CAC_QUYET_DINH: readonly QuyetDinhAudit[] = ['ALLOW', 'DENY'];
export const CAC_NGUON: readonly NguonAudit[] = ['gateway', 'service', 'keycloak'];
export const CAC_NHOM: readonly NhomTheo[] = ['user', 'ip', 'tenant'];
export const CAC_MUC: readonly MucDoThongBao[] = ['INFO', 'WARNING', 'CRITICAL'];

/** Định dạng của hợp đồng API — backend kiểm lại đúng các mẫu này. */
const MAU_ID = /^[a-z0-9][a-z0-9_-]{0,39}$/;
const MAU_ACTION = /^[A-Za-z0-9_.:/*-]{1,100}$/;
const MAU_MA_LOI = /^[A-Z][A-Z0-9_]{1,59}$/;
const DAI_TEN = 100;
const DAI_RESOURCE = 200;
const DAI_ID = 40;

/**
 * Quy tắc mới điền sẵn theo ví dụ của ADR-0059 mục 4.1: "hơn 20 lần DENY của
 * một user trong 5 phút". `id` để trống — sinh từ tên khi lưu vào danh sách.
 */
export function quyTacMoi(): QuyTacCanhBao {
  return {
    id: '',
    name: '',
    enabled: true,
    decision: 'DENY',
    actions: [],
    resource_prefix: null,
    sources: [],
    error_codes: [],
    group_by: 'user',
    threshold: 20,
    window_seconds: 300,
    cooldown_seconds: 900,
    severity: 'WARNING',
  };
}

// ---------------------------------------------------------------------------
// Mã quy tắc
// ---------------------------------------------------------------------------

/** Tên → slug ASCII: bỏ dấu, `đ` → `d`, ký tự lạ thành `-`, tối đa 40 ký tự. */
export function slugTuTen(ten: string): string {
  return ten
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, DAI_ID)
    .replace(/-+$/, '');
}

/**
 * Mã cho quy tắc mới: slug của tên, trùng thì thêm `-2`, `-3`… Tên không còn
 * ký tự ASCII nào (toàn ký hiệu) thì dùng `quy-tac`.
 */
export function sinhId(ten: string, daCo: Iterable<string>): string {
  const tap = new Set(daCo);
  const goc = slugTuTen(ten) || 'quy-tac';
  if (!tap.has(goc)) return goc;
  for (let n = 2; ; n++) {
    const duoi = `-${n}`;
    const id = `${goc.slice(0, DAI_ID - duoi.length).replace(/-+$/, '')}${duoi}`;
    if (!tap.has(id)) return id;
  }
}

// ---------------------------------------------------------------------------
// Chuẩn hoá và kiểm
// ---------------------------------------------------------------------------

const boTrung = <T>(ds: readonly T[]): T[] => [...new Set(ds)];

/** Chuẩn hoá như server: cắt khoảng trắng, chuỗi rỗng thành `null`, bỏ phần tử trùng. */
export function chuanHoaQuyTac(q: QuyTacCanhBao): QuyTacCanhBao {
  const tienTo = q.resource_prefix?.trim() ?? '';
  return {
    ...q,
    name: q.name.trim(),
    actions: boTrung(q.actions.map((s) => s.trim()).filter(Boolean)),
    resource_prefix: tienTo === '' ? null : tienTo,
    sources: CAC_NGUON.filter((n) => q.sources.includes(n)),
    error_codes: boTrung(q.error_codes.map((s) => s.trim().toUpperCase()).filter(Boolean)),
  };
}

export type MaLoiQuyTac =
  | 'bat_buoc'
  | 'qua_dai'
  | 'id_sai'
  | 'id_trung'
  | 'khong_phai_so_nguyen'
  | 'nho_hon_min'
  | 'lon_hon_max'
  | 'qua_so_muc'
  | 'muc_sai'
  | 'ngoai_danh_sach';

export interface LoiQuyTac {
  ma: MaLoiQuyTac;
  thamSo?: Record<string, string | number>;
}

export type LoiCacTruong = Partial<Record<TruongQuyTac, LoiQuyTac>>;

/** Thứ tự trường trên form — lỗi đầu tiên theo thứ tự này là lỗi báo ở cấp danh sách. */
export const THU_TU_TRUONG: readonly TruongQuyTac[] = [
  'name',
  'id',
  'enabled',
  'decision',
  'actions',
  'resource_prefix',
  'sources',
  'error_codes',
  'group_by',
  'threshold',
  'window_seconds',
  'cooldown_seconds',
  'severity',
];

function kiemSoNguyen(n: unknown, bien: BienTruong | undefined): LoiQuyTac | null {
  if (typeof n !== 'number' || !Number.isFinite(n)) return { ma: 'bat_buoc' };
  if (!Number.isInteger(n)) return { ma: 'khong_phai_so_nguyen' };
  if (bien?.min !== null && bien?.min !== undefined && n < bien.min) {
    return { ma: 'nho_hon_min', thamSo: { min: bien.min } };
  }
  if (bien?.max !== null && bien?.max !== undefined && n > bien.max) {
    return { ma: 'lon_hon_max', thamSo: { max: bien.max } };
  }
  return null;
}

function kiemDanhSach(ds: unknown, mau: RegExp, bien: BienTruong | undefined): LoiQuyTac | null {
  if (!Array.isArray(ds)) return { ma: 'bat_buoc' };
  if (bien?.max_items && ds.length > bien.max_items) {
    return { ma: 'qua_so_muc', thamSo: { max: bien.max_items } };
  }
  const sai = (ds as unknown[]).find((s) => typeof s !== 'string' || !mau.test(s));
  return sai === undefined ? null : { ma: 'muc_sai', thamSo: { giaTri: String(sai) } };
}

/**
 * Kiểm một quy tắc theo hợp đồng và biên `bounds.fields` của danh mục.
 *
 * `idKhac` là mã của **các quy tắc còn lại** trong danh sách. Đây là UX, không
 * phải chốt chặn: backend kiểm lại và trả lỗi theo trường.
 */
export function kiemQuyTac(
  q: QuyTacCanhBao,
  bien: Record<string, BienTruong> | null | undefined,
  idKhac: ReadonlySet<string>,
): LoiCacTruong {
  const b = bien ?? {};
  const loi: LoiCacTruong = {};

  const ten = typeof q.name === 'string' ? q.name.trim() : '';
  if (!ten) loi.name = { ma: 'bat_buoc' };
  else if (ten.length > DAI_TEN) loi.name = { ma: 'qua_dai', thamSo: { max: DAI_TEN } };

  if (!MAU_ID.test(q.id ?? '')) loi.id = { ma: 'id_sai' };
  else if (idKhac.has(q.id)) loi.id = { ma: 'id_trung', thamSo: { giaTri: q.id } };

  if (q.decision !== null && !CAC_QUYET_DINH.includes(q.decision)) loi.decision = { ma: 'ngoai_danh_sach' };

  const action = kiemDanhSach(q.actions, MAU_ACTION, b.actions);
  if (action) loi.actions = action;

  if (q.resource_prefix !== null && q.resource_prefix.length > DAI_RESOURCE) {
    loi.resource_prefix = { ma: 'qua_dai', thamSo: { max: DAI_RESOURCE } };
  }

  if (!Array.isArray(q.sources) || q.sources.some((n) => !CAC_NGUON.includes(n))) {
    loi.sources = { ma: 'ngoai_danh_sach' };
  }

  const maLoi = kiemDanhSach(q.error_codes, MAU_MA_LOI, b.error_codes);
  if (maLoi) loi.error_codes = maLoi;

  if (!CAC_NHOM.includes(q.group_by)) loi.group_by = { ma: 'ngoai_danh_sach' };
  if (!CAC_MUC.includes(q.severity)) loi.severity = { ma: 'ngoai_danh_sach' };

  for (const truong of ['threshold', 'window_seconds', 'cooldown_seconds'] as const) {
    const l = kiemSoNguyen(q[truong], b[truong]);
    if (l) loi[truong] = l;
  }
  return loi;
}

/** Lỗi đầu tiên theo thứ tự trên form, hoặc `null` khi quy tắc hợp lệ. */
export function loiDauTien(loi: LoiCacTruong): { truong: TruongQuyTac; loi: LoiQuyTac } | null {
  for (const truong of THU_TU_TRUONG) {
    const l = loi[truong];
    if (l) return { truong, loi: l };
  }
  return null;
}

/** Kết quả kiểm cả danh sách — dùng ở `kiemGiaTri` của form cài đặt. */
export type LoiDanhSachQuyTac =
  | { ma: 'kieu_sai' }
  | { ma: 'qua_so_quy_tac'; max: number }
  | { ma: 'quy_tac_sai'; chiSo: number; ten: string; truong: TruongQuyTac; loi: LoiQuyTac };

export function kiemDanhSachQuyTac(
  giaTri: unknown,
  bien: { max_items?: number | null; fields?: Record<string, BienTruong> | null } | null | undefined,
): LoiDanhSachQuyTac | null {
  if (!Array.isArray(giaTri)) return { ma: 'kieu_sai' };
  const ds = giaTri as QuyTacCanhBao[];
  if (bien?.max_items && ds.length > bien.max_items) return { ma: 'qua_so_quy_tac', max: bien.max_items };
  for (let i = 0; i < ds.length; i++) {
    const q = ds[i];
    if (!q || typeof q !== 'object') return { ma: 'kieu_sai' };
    const idKhac = new Set(ds.filter((_x, j) => j !== i).map((x) => x.id));
    const dau = loiDauTien(kiemQuyTac(q, bien?.fields, idKhac));
    if (dau) return { ma: 'quy_tac_sai', chiSo: i, ten: q.name, truong: dau.truong, loi: dau.loi };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Thêm, sửa, xoá trong mảng — không sửa tại chỗ
// ---------------------------------------------------------------------------

export function themQuyTac(ds: readonly QuyTacCanhBao[], q: QuyTacCanhBao): QuyTacCanhBao[] {
  return [...ds, q];
}

/** Thay quy tắc mang mã `id` bằng `q`, giữ nguyên vị trí. */
export function suaQuyTac(ds: readonly QuyTacCanhBao[], id: string, q: QuyTacCanhBao): QuyTacCanhBao[] {
  return ds.map((x) => (x.id === id ? q : x));
}

export function xoaQuyTac(ds: readonly QuyTacCanhBao[], id: string): QuyTacCanhBao[] {
  return ds.filter((x) => x.id !== id);
}

export function batTatQuyTac(ds: readonly QuyTacCanhBao[], id: string, bat: boolean): QuyTacCanhBao[] {
  return ds.map((x) => (x.id === id && x.enabled !== bat ? { ...x, enabled: bat } : x));
}

// ---------------------------------------------------------------------------
// Tóm tắt để hiện
// ---------------------------------------------------------------------------

export type PhanDieuKien =
  | { loai: 'quyet_dinh'; giaTri: QuyetDinhAudit }
  | { loai: 'action'; ds: string[] }
  | { loai: 'resource'; tienTo: string }
  | { loai: 'nguon'; ds: NguonAudit[] }
  | { loai: 'ma_loi'; ds: string[] }
  /** Không lọc gì: mọi sự kiện audit đều khớp. */
  | { loai: 'moi_su_kien' };

/** Các vế của điều kiện, theo thứ tự đọc; vế không lọc gì thì bỏ. */
export function tomTatDieuKien(q: QuyTacCanhBao): PhanDieuKien[] {
  const ra: PhanDieuKien[] = [];
  if (q.decision) ra.push({ loai: 'quyet_dinh', giaTri: q.decision });
  if (q.actions.length > 0) ra.push({ loai: 'action', ds: q.actions });
  if (q.resource_prefix) ra.push({ loai: 'resource', tienTo: q.resource_prefix });
  if (q.sources.length > 0) ra.push({ loai: 'nguon', ds: q.sources });
  if (q.error_codes.length > 0) ra.push({ loai: 'ma_loi', ds: q.error_codes });
  return ra.length > 0 ? ra : [{ loai: 'moi_su_kien' }];
}

export type DonViThoiLuong = 'giay' | 'phut' | 'gio' | 'ngay';

/** `300` → 5 phút, `86400` → 1 ngày, `90` → 90 giây: đơn vị lớn nhất chia hết. */
export function tachThoiLuong(giay: number): { so: number; donVi: DonViThoiLuong } {
  if (giay > 0 && giay % 86_400 === 0) return { so: giay / 86_400, donVi: 'ngay' };
  if (giay > 0 && giay % 3_600 === 0) return { so: giay / 3_600, donVi: 'gio' };
  if (giay > 0 && giay % 60 === 0) return { so: giay / 60, donVi: 'phut' };
  return { so: giay, donVi: 'giay' };
}

/**
 * So hai danh sách theo `id`: quy tắc thêm, bỏ, sửa. Cả ba rỗng mà hai danh
 * sách khác nhau thì chỉ đổi thứ tự.
 */
export function soSanhQuyTac(
  truoc: readonly QuyTacCanhBao[] | null | undefined,
  sau: readonly QuyTacCanhBao[] | null | undefined,
): { them: QuyTacCanhBao[]; bo: QuyTacCanhBao[]; sua: QuyTacCanhBao[] } {
  const a = new Map((truoc ?? []).map((q) => [q.id, q]));
  const b = new Map((sau ?? []).map((q) => [q.id, q]));
  return {
    them: [...b.values()].filter((q) => !a.has(q.id)),
    bo: [...a.values()].filter((q) => !b.has(q.id)),
    sua: [...b.values()].filter((q) => a.has(q.id) && JSON.stringify(a.get(q.id)) !== JSON.stringify(q)),
  };
}

// ---------------------------------------------------------------------------
// Lỗi theo trường của server
// ---------------------------------------------------------------------------

export interface LoiMayChuQuyTac {
  /** Trường bị lỗi; `null` khi lỗi trỏ vào cả quy tắc. */
  truong: string | null;
  cau: string;
}

/**
 * Gom lỗi `INVALID_ARGUMENT` của server theo chỉ số quy tắc. Con trỏ dạng
 * `#/settings/audit.alert_rules/<i>/<trường>` (có hay không có `#`). Lỗi trỏ
 * vào cả khoá, không vào quy tắc nào, thì không có ở đây — form hiện ở khối.
 */
export function loiMayChuTheoQuyTac(
  loi: ReadonlyArray<{ pointer: string; detail: string }>,
  khoa: string,
): Map<number, LoiMayChuQuyTac[]> {
  const ra = new Map<number, LoiMayChuQuyTac[]>();
  const tienTo = `/settings/${khoa.replace(/~/g, '~0').replace(/\//g, '~1')}/`;
  for (const l of loi) {
    const p = l.pointer.replace(/^#/, '');
    if (!p.startsWith(tienTo)) continue;
    const [chiSo, truong] = p.slice(tienTo.length).split('/');
    if (!chiSo || !/^\d+$/.test(chiSo)) continue;
    const i = Number(chiSo);
    const ds = ra.get(i) ?? [];
    ds.push({ truong: truong ?? null, cau: l.detail });
    ra.set(i, ds);
  }
  return ra;
}
